import {
  ErrorResponse,
  GenerateOtpResponse,
  ResponseOfOtpResend,
  ResponseOfOtpVerify,
  UpdatePasswordResponse,
  UserResponse,
  VerifyOtpResponse,
  ChallengeIdResponse,
  GetSocialList,
  GetSocialListExternalIdentitiesInner,
  WorkflowResponse,
} from '../services';
import { WorkflowTypeEnum } from './enums/identityEnums';

export interface BaseUrlReqPayloadType {
  baseUrl: string;
}

export interface ErrorHandlers {
  errorHandlers?: Record<number, ErrorHandlerFunctionType>;
}

export type ErrorHandlerFunctionType = (res: Response) => void;

export type GenotpErrorResponseHandler = {
  errorHandlers?: Record<number, ErrorHandlerFunctionType>;
};

export type CorrelationidType = {
  correlationid?: string;
};

export interface ChallengeIdReqType {
  challengeId?: string;
}

export interface SocialAccountReqType {
  provider?: string;
  profileId?: string;
}

export interface ProfileIdReqType {
  profileId?: string;
}
export interface ProfileIdResType {
  profileId?: string;
}

export interface WorkflowRequest {
  workflowType: WorkflowTypeEnum;
  username: string;
  password?: string;
  idNumber?: string;
}

export interface UpdateWorkflowRequest {
  selectedProfile: string;
  username: string;
  version: number;
}

export interface SubmitWorkflowRequest {
  challengeId: string;
  password: string;
}

export interface GetAuthorizeUrlType {
  gluuBaseUrl: string;
  redirectUri: string;
  clientId: string;
  scope?: string;
  responseType?: string;
  acrValues?: string;
  state?: string;
  prompt?: string;
  loginHint?: string;
  nonce?: string;
  countryIso?: string;
  bcAuthnToken?: string;
  socialProvider?: string;
  preselectedExternalProvider?: string;
  socialConnectRedirectUrl?: string;
  responseMode?: string;
  jansKey?: string;
}

export interface GenerateOtp {
  otpModes: {
    modeValue: string;
    modeType: string;
  }[];
  otpLength: string;
}
export interface GenerateOtpProps {
  payload: GenerateOtp;
}

export interface OtpErrorResponse {
  timestamp: string;
  type: string;
  instance: string;
  subDetails: Object;
  status: number;
  detail: string;
  code: string;
}

export interface VerifyOtpReq {
  otp: string;
}

export interface VerifyOtpProps {
  payload: VerifyOtpReq;
  requestId: string;
}

export type VerifyOtpResponseType = VerifyOtpResponse | OtpErrorResponse;

export interface ResendOtpProps {
  requestId: string;
}

export interface CheckUserExistanceReqType {
  projection: string;
  username: string;
}

export interface CreatePasswordReqPayloadType {
  password: string;
}

export interface SocialAccountReqPayloadType {
  profileId: string;
}

export interface CreatePasswordReqType {
  username: string;
  payload: CreatePasswordReqPayloadType;
}

export interface CreatePasswordResPropType {
  inum: string;
  username: string;
  status: boolean;
}
export interface CreatePasswordResType {
  response: CreatePasswordResPropType;
  message: string;
}

export interface CheckUserExistanceResType {
  isExist?: boolean;
  active?: boolean;
}

export interface CheckUserExistanceInumResType {
  inum: string;
}

export interface ForgotAmwayIdReqPayload {
  username: string;
}

export interface ForgotAmwayIdResPayload {
  inum: string;
  username: string;
  active: boolean;
  source: string;
}

export interface LogoutReqPayload {
  gluuBaseUrl: string;
  idTokenHint?: string;
  postLogoutRedirectUri?: string;
  sessionState?: string;
}

export interface AuthenticationReqObj {
  username: string;
  password: string;
  clientId: string;
  redirectUri: string;
  state?: string;
}

export type SharedData = {
  requestId?: string;
};

export type UserData = {
  username?: string;
  countryCode?: string;
  email?: string;
  phone?: string;
};

export type EnteredOtp = {
  input0: string;
  input1: string;
  input2: string;
  input3: string;
  input4: string;
  input5: string;
};

export type OtpData = {
  requestId?: string;
  isOtpSent?: boolean;
  isVerified?: boolean;
  isChecking?: boolean;
  isInvalid?: boolean;
  otp?: EnteredOtp;
  challengeId?: string;
  username?: string;
};

export type ForgotUsernameData = {
  isUsernameSent?: boolean;
  showHelpModal?: boolean;
};

export type ForgotPasswordData = {
  isOtpSent?: boolean;
  isOtpVerified?: boolean;
  currentPage?: string;
  showHelpModal?: boolean;
  showNeedMoreHelp?: boolean;
  showDivider?: boolean;
  showHocFooterBtn?: boolean;
  showBackToSignin?: boolean;
};

export type ResendClockData = {
  threshold?: number;
  secondsLeft?: number;
};

export type SendOtpData = {
  isNextEnable?: boolean;
  hasError?: boolean;
};

export type UpdatePasswordData = {
  isLoading?: boolean;
  hasError?: boolean;
  enableChangePasswordBtn: boolean;
  isPasswordChanged: boolean;
  currentPassword?: string;
  confirmPassword?: string;
  createPassword?: string;
};

export type UpdateUsernameData = {
  isNextEnable: boolean;
  hasError: boolean;
};

export type SocialAccountDelinkPageData = {
  isLoading?: boolean;
  hasError?: boolean;
  socialAccountList?: Array<GetSocialListExternalIdentitiesInner>;
};

export enum ForgotPasswordComponentKeyEnum {
  USER_INFO_FORM = 'UserInfoForm',
  CREATE_PASSWORD_FORM = 'CreatePasswordForm',
  SUCCESS_NOTIFICATION = 'SuccessNotification',
}

export interface ForgotPasswordPageDataType {
  isNextEnable?: boolean;
  hasError?: boolean;
  isLoading?: boolean;
  currentPage?: ForgotPasswordComponentKeyEnum;
  username?: string;
  email?: string;
  countryCode?: string;
  phone?: string;
  workflowId?: string;
  challengeId?: string;
  showHelpModal?: boolean;
}

export interface LoginPageData {
  shouldLoginButtonBeEnable?: boolean;
}

export type InitialIdentityState = {
  isLoading: boolean;
  hasError: boolean;
  error: unknown;
  sharedData?: SharedData;
  updatePasswordData: UpdatePasswordData;
  userData: UserData;
  otpData: OtpData;
  forgotUsername: ForgotUsernameData;
  forgotPassword?: ForgotPasswordData;
  resendClockData: ResendClockData;
  sendOtpData: SendOtpData;
  usernameData: UpdateUsernameData;
  socialAccountDelinkPageData: SocialAccountDelinkPageData;
  forgotPasswordPageData?: ForgotPasswordPageDataType;
  loginPageData?: LoginPageData;
};

export interface GenereateWorkflowResponse {
  profileList: Profile[];
  workflowId: string;
  challengeId?: string;
  version: number;
}

export interface UpdateWorkflowResponse {
  workflowId: string;
  challengeId: string;
  version: number;
  stepTwoChallengeId?: string;
}

export interface Profile {
  displayName: { familyName: string; givenName: string };
  profileId: string;
  isUserMigrated: boolean;
  profileContact?: string;
}
export interface ChallengeResponse {
  modeValue?: string;
  modeType?: string;
  challengeVerified?: boolean;
}

export interface CreateSocialLinkChallengeRequest {
  aboNumber: string;
  idNumber: string;
  socialJansKey: string;
}

export interface CreateSocialLinkChallengeResponse {
  challengeId?: string;
  modeValue?: string;
}

export interface SubmitSocialLinkChallengeRequest {
  challengeId: string;
  socialJansKey: string;
}

export interface AccountData {
  version: number;
  affiliate: string;
  lastSyncDate: string;
  accountNumber: number;
  name: {
    name: string;
    characterType: string;
  };
  secondaryNames: [
    {
      name: string;
      characterType: string;
    },
  ];
  legalEntity: {
    type: string;
  };
  accountType: string;
  status: string;
  languageCode: string;
  loaCode: string;
  entryDate: string;
  expirationDate: string;
  countryCode: string;
  renewalFlag: string;
  renewalStatus: string;
  renewalLifecycle: string;
  businessNatureLifeCycle: string;
  initialLifeCycle: string;
  createdAt: string;
  createdBy: string;
  lastUpdatedAt: string;
  lastUpdatedBy: string;
}

export interface AccountListResponse {
  accountList: AccountData[];
}

export interface IdentityApiResponse
  extends UpdatePasswordResponse,
    ChallengeIdResponse,
    GenerateOtpResponse,
    VerifyOtpResponse,
    ResponseOfOtpResend,
    ForgotAmwayIdResPayload,
    VerifyOtpResponse,
    ResponseOfOtpVerify,
    GetSocialList,
    UserResponse,
    WorkflowResponse {}

export type Identity = Partial<IdentityApiResponse>;

export type IdentityResponse = Identity | ErrorResponse | unknown;
export interface CustomRequestOptions {
  headers?: {
    [key: string]: string;
  };
  baseURL?: string;
}
